/**
 * BubbleChartControl
 *
 * Bubble chart with X, Y, and size dimensions
 * Three-dimensional data visualization
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData, getSeriesConfig } from './BaseChartComponent';

const BubbleChartControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);
  const seriesConfig = useMemo(() => getSeriesConfig(control), [control]);

  const bubbleConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      xAxisKey: (config?.xAxisKey as string) || 'x',
      yAxisKey: (config?.yAxisKey as string) || 'y',
      sizeKey: (config?.sizeKey as string) || 'size',
      maxBubbleSize: (config?.maxBubbleSize as number) || 80,
    };
  }, [control.config]);

  const renderBubbles = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const padding = 40;
    const width = 600;
    const height = (control.config?.height as number) || 400;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    // Extract values
    const xValues: number[] = [];
    const yValues: number[] = [];
    const sizeValues: number[] = [];

    chartData.forEach((d) => {
      const x = (d as Record<string, unknown>)[bubbleConfig.xAxisKey];
      const y = (d as Record<string, unknown>)[bubbleConfig.yAxisKey];
      const size = (d as Record<string, unknown>)[bubbleConfig.sizeKey];
      if (typeof x === 'number') xValues.push(x);
      if (typeof y === 'number') yValues.push(y);
      if (typeof size === 'number') sizeValues.push(size);
    });

    const xMin = Math.min(...xValues, 0);
    const xMax = Math.max(...xValues, 1);
    const yMin = Math.min(...yValues, 0);
    const yMax = Math.max(...yValues, 1);
    const maxSize = Math.max(...sizeValues, 1);

    const xRange = xMax - xMin || 1;
    const yRange = yMax - yMin || 1;

    const xScale = plotWidth / xRange;
    const yScale = plotHeight / yRange;

    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c'];

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - padding - ratio * plotHeight;
          return (
            <line
              key={`h-grid-${ratio}`}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const x = padding + ratio * plotWidth;
          return (
            <line
              key={`v-grid-${ratio}`}
              x1={x}
              y1={padding}
              x2={x}
              y2={height - padding}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}

        {/* Axes */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#000" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#000" />

        {/* Bubbles */}
        {chartData.map((d, index) => {
          const x = (d as Record<string, unknown>)[bubbleConfig.xAxisKey];
          const y = (d as Record<string, unknown>)[bubbleConfig.yAxisKey];
          const size = (d as Record<string, unknown>)[bubbleConfig.sizeKey];

          if (typeof x !== 'number' || typeof y !== 'number' || typeof size !== 'number') {
            return null;
          }

          const bubbleX = padding + (x - xMin) * xScale;
          const bubbleY = height - padding - (y - yMin) * yScale;
          const bubbleRadius = (size / maxSize) * (bubbleConfig.maxBubbleSize / 2);
          const color = colors[index % colors.length];

          return (
            <circle
              key={`bubble-${index}`}
              cx={bubbleX}
              cy={bubbleY}
              r={Math.max(bubbleRadius, 3)}
              fill={color}
              opacity="0.6"
              stroke={color}
              strokeWidth="1"
            />
          );
        })}

        {/* Axis labels */}
        {[0, 0.5, 1].map((ratio) => {
          const xLabelX = padding + ratio * plotWidth;
          const xValue = Math.round(xMin + ratio * xRange);
          const yLabelY = height - padding - ratio * plotHeight;
          const yValue = Math.round(yMin + ratio * yRange);

          return (
            <g key={`label-${ratio}`}>
              <text
                x={xLabelX}
                y={height - padding + 20}
                textAnchor="middle"
                fontSize="12"
                fill="#6b7280"
              >
                {xValue}
              </text>
              <text
                x={padding - 10}
                y={yLabelY}
                textAnchor="end"
                fontSize="12"
                fill="#6b7280"
              >
                {yValue}
              </text>
            </g>
          );
        })}
      </svg>
    );
  }, [chartData, bubbleConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="bubble-chart">
      <div style={{ width: '100%', height: '100%' }}>{renderBubbles}</div>
    </BaseChartComponent>
  );
});

BubbleChartControl.displayName = 'BubbleChartControl';
export default BubbleChartControl;
